import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useVaultDisplayName } from "../../hooks/useVaultDisplayName";
import { useVault } from "../../hooks/useVault";
import { useSession } from "../../stores/session";
import { useMasterSession } from "../../stores/masterSession";
import { VaultSwitcher } from "./VaultSwitcher";

// Sidebar shell. The vault switcher + add-vault button were removed
// in the master-password rewrite. PR 4b reintroduces both as UX
// surfaced under the master session — adding S3 vaults is a
// post-master-unlock action, and switching between synced vaults no
// longer prompts for a per-vault passphrase.

export function Sidebar() {
  const vault = useVault();
  const navigate = useNavigate();
  const session = useSession((s) => s.session);
  const clearSession = useSession((s) => s.clear);
  const clearMaster = useMasterSession((s) => s.clear);
  const displayName = useVaultDisplayName().data ?? "personal";
  const queryClient = useQueryClient();

  async function lockNow() {
    if (!session) return;
    // Order matters: navigate FIRST so the Browser (mounted at the
    // current vault's URL) unmounts before we clear the session.
    // Otherwise its refresh-guard re-renders with session=null while
    // still holding the vaultId param and redirects to the current
    // vault's unlock screen, clobbering this navigation.
    const handle = session.handle;
    navigate("/master");
    queryClient.clear();
    clearSession();
    clearMaster();
    try {
      await vault.lockVault(handle);
    } catch (err) {
      toast.error(`Failed to lock: ${String(err)}`);
    }
  }

  return (
    <aside className="flex w-full flex-col bg-bg-sidebar p-3 text-sm md:w-60 md:border-r md:border-border">
      <div className="mb-5 flex items-center gap-2 px-2 font-semibold">
        <div className="h-5 w-5 rounded-md bg-gradient-to-br from-accent-soft to-accent" />
        vaultwisp
      </div>

      <VaultSwitcher />

      {session && (
        <>
          <div className="mt-4 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-text-dim">
            {displayName}
          </div>
          <nav className="flex flex-col gap-0.5 pt-1">
            <Link
              to={`/v/${session.vaultId}/~/`}
              className="flex min-h-12 items-center rounded-md px-2 py-1.5 text-text-soft hover:bg-accent-bg hover:text-text md:min-h-0"
            >
              All files
            </Link>
            <Link
              to="/settings"
              className="flex min-h-12 items-center rounded-md px-2 py-1.5 text-text-soft hover:bg-accent-bg hover:text-text md:min-h-0"
            >
              Settings
            </Link>
          </nav>
        </>
      )}

      <div className="mt-auto flex items-center gap-2 border-t border-border pt-3 text-xs text-text-dim">
        <span className="h-2 w-2 rounded-full bg-ok" />
        <button onClick={lockNow} className="font-medium text-text-soft hover:text-text">
          Lock now
        </button>
      </div>
    </aside>
  );
}
